/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { makeObservable, observable, runInAction } from "mobx"
import MedaicaApiService, { isMedaicaApiError, MedaicaApiErrorType } from "@medaica/common/services/medaica-api-service"
import AccessTokenStorage from "@medaica/common/services/access-token-storage"
import { BookingAccount, BookingConfig } from "@medaica/common/types"
import { logError } from "@medaica/common/services/util"
import { addSeconds } from "date-fns"
import createBookingApi from "../api/create-booking-api"
import { BookingApiClient } from "../api/booking-api-client"
import BookingUiStore, { BookingView } from "./booking-ui-store"

type EntryParams = { token?: string; email?: string }

const SESSION_STORAGE_KEY = "session"
const CODE_TTL_SECONDS = 10 * 60

// Only mid-wizard steps are worth resuming to after a session-expiry re-verify; an
// expiry at a gate/entry view has no input to preserve and must NOT become the target.
const RESUMABLE_VIEWS = new Set<BookingView>(["patient", "consent", "health", "time"])

const isSessionExpiry = (type: MedaicaApiErrorType): boolean =>
  type === MedaicaApiErrorType.EXPIRED_SESSION || type === MedaicaApiErrorType.SIGNATURE_HAS_EXPIRED

/**
 * Owns the verify gate + buyer session (05 §1/§4). Pre-session it holds the
 * masked email + verificationToken from `createSession`; after a correct code it
 * holds the 30-min session token (persisted via `AccessTokenStorage` so a refresh
 * lands back on the account, not the gate). On a mid-wizard session expiry it
 * stashes `resumeView`, leaves the `wizardStore` untouched, re-mints a fresh code
 * and routes to Verify; the next correct code returns the user to where they were.
 */
class BookingSessionStore {
  maskedEmail: string | null = null
  verificationToken: string | null = null
  codeExpiresAt: Date | null = null
  session: { token: string; expiresAt: Date } | null = null
  account: BookingAccount | null = null
  config: BookingConfig | null = null
  accountLoadError = false
  resumeView: BookingView | null = null
  entryMode: "token" | "email" | null = null

  readonly api: BookingApiClient
  private readonly _uiStore: BookingUiStore
  // sessionStorage (not localStorage): the anonymous session token is tab-scoped and dies
  // with the tab, so it can't linger on shared computers. F5 in the same tab still restores.
  private readonly _storage = new AccessTokenStorage("D2C_BOOKING_TOKENS", sessionStorage)
  private _entryParams: EntryParams | null = null

  constructor(uiStore: BookingUiStore) {
    this._uiStore = uiStore
    this.api = createBookingApi(() => new MedaicaApiService(() => Promise.resolve(this.session?.token)).booking)

    makeObservable(this, {
      maskedEmail: observable,
      verificationToken: observable,
      codeExpiresAt: observable,
      session: observable,
      account: observable,
      config: observable,
      accountLoadError: observable,
      resumeView: observable,
      entryMode: observable,
    })

    this.restoreSession()
  }

  // Time-dependent, so a plain getter (not a cached computed).
  get isAuthenticated(): boolean {
    return !!this.session && new Date() < this.session.expiresAt
  }

  private restoreSession(): void {
    const stored = this._storage.get(SESSION_STORAGE_KEY)
    // AccessTokenStorage round-trips through JSON and its reviver only revives Maps,
    // so expiresAt comes back as an ISO string — re-hydrate to a Date, or the
    // Date < string comparison is NaN-false and a live session is never restored.
    if (stored && new Date() < new Date(stored.expiresAt)) {
      runInAction(() => {
        this.session = { token: stored.value, expiresAt: new Date(stored.expiresAt) }
      })
    }
  }

  private persistSession(): void {
    if (this.session) {
      this._storage.set(SESSION_STORAGE_KEY, {
        value: this.session.token,
        expiresAt: this.session.expiresAt,
        userId: this.maskedEmail ?? "buyer",
      })
    }
  }

  // Evict the current session from memory AND storage, so nothing behind the gate
  // (session token or the loaded account) survives for the next visit on this device.
  private clearSession(): void {
    runInAction(() => {
      this.session = null
      this.account = null
    })
    this._storage.remove(SESSION_STORAGE_KEY)
  }

  async startSession(params: EntryParams): Promise<void> {
    // A fresh verification supersedes any session already cached in this browser, so
    // evict it (in-memory + persisted) before minting a new code. On a shared/kiosk
    // device this stops a second buyer's link from ever resolving to — or leaving
    // behind — the previous buyer's session (08 §1).
    this.clearSession()
    this._entryParams = params
    const result = await this.api.createSession(params)
    runInAction(() => {
      this.maskedEmail = result.maskedEmail
      this.verificationToken = result.verificationToken
      this.codeExpiresAt = result.codeExpiresAt
      this.entryMode = params.token ? "token" : "email"
    })
    this._uiStore.goTo("verify")
  }

  async resendCode(): Promise<void> {
    if (!this.verificationToken) {
      return
    }
    await this.api.resendCode(this.verificationToken)
    runInAction(() => {
      this.codeExpiresAt = addSeconds(new Date(), CODE_TTL_SECONDS)
    })
  }

  async submitCode(code: string): Promise<void> {
    if (!this.verificationToken) {
      throw new Error("No verification is in progress")
    }
    const result = await this.api.verify(this.verificationToken, code)
    runInAction(() => {
      this.session = { token: result.accessToken, expiresAt: addSeconds(new Date(), result.expiresIn) }
    })
    this.persistSession()
    await this.loadAccount()
    // loadAccount can hit an expiry that clears the session and re-routes to the gate;
    // don't then force-navigate to account (which would strand on a null-account spinner).
    if (!this.isAuthenticated) {
      return
    }
    void this.loadConfig() // preload the booking window/exam config for the Time step
    const target = this.resumeView ?? "account"
    runInAction(() => {
      this.resumeView = null
    })
    this._uiStore.goTo(target)
  }

  async loadAccount(): Promise<void> {
    // Wrapped so the raw callers (init effect, Done, the NO_CREDITS branch) inherit
    // the session-expiry resume; other errors are logged, never thrown, so the
    // fire-and-forget background refreshes can't leak an unhandled rejection. A
    // non-expiry failure sets `accountLoadError` so the Account screen can offer a
    // retry instead of stranding the user on a permanent spinner (a null account).
    runInAction(() => {
      this.accountLoadError = false
    })
    try {
      const account = await this.runSessionCall(() => this.api.getAccount(), this._uiStore.view)
      if (account) {
        runInAction(() => {
          this.account = account
        })
      }
    } catch (error) {
      logError(error)
      runInAction(() => {
        this.accountLoadError = true
      })
    }
  }

  // Fetch the booking window/exam config once (03 §4) — the single source of truth for the
  // Time step's lead/horizon/slot sizing. Guarded so the verify + init callers don't double-
  // fetch; a plain try/catch (not runSessionCall) since it's non-critical static data — a
  // session expiry is caught by the Time step's slot fetch, which owns the re-verify resume.
  async loadConfig(): Promise<void> {
    if (this.config) {
      return
    }
    try {
      const config = await this.api.getBookingConfig()
      runInAction(() => {
        this.config = config
      })
    } catch (error) {
      logError(error)
    }
  }

  /**
   * Wrap a session-scoped API call. On a 30-min session expiry (05 §1) it stashes
   * the current step, keeps the wizard input, re-mints a fresh code and routes to
   * Verify (the next correct code resumes at `resumeView`), and returns null.
   * Any other error propagates to the caller (e.g. SLOT_TAKEN).
   */
  async runSessionCall<T>(call: () => Promise<T>, currentView: BookingView): Promise<T | null> {
    try {
      return await call()
    } catch (error) {
      if (isMedaicaApiError(error) && isSessionExpiry((error as { type: MedaicaApiErrorType }).type)) {
        this.handleExpiredSession(currentView)
        await this.reVerify()
        return null
      }
      throw error
    }
  }

  private handleExpiredSession(currentView: BookingView): void {
    runInAction(() => {
      // Guard against setting a gate/entry view (e.g. emailEntry during init) as the
      // resume target — otherwise a successful re-verify would route back to the gate
      // instead of the account. Only real wizard steps are resumable.
      this.resumeView = RESUMABLE_VIEWS.has(currentView) ? currentView : null
      this.session = null
      this.account = null
    })
    // Drop the persisted token too — it's known-invalid now, so restoring it on the next
    // refresh would only burn a getAccount round-trip to re-discover the expiry. Removing
    // it lets a bare-`/schedule` refresh go straight to the gate (a fresh token is re-persisted
    // by `startSession`/`submitCode` once the user re-verifies).
    this._storage.remove(SESSION_STORAGE_KEY)
  }

  // Re-mint a code and return to the gate. With an entry token/email we can send a
  // fresh code straight to Verify; a session restored from storage has neither, so
  // fall back to the always-recoverable email-entry step. Either way the wizard
  // input and resumeView survive, so the next correct code resumes the step.
  private async reVerify(): Promise<void> {
    if (this._entryParams) {
      try {
        await this.startSession(this._entryParams)
        return
      } catch (error) {
        logError(error)
      }
    }
    this._uiStore.goTo("emailEntry")
  }
}

export default BookingSessionStore
